import { allMusicServices } from 'fym-shared/src/music-services/allMusicServices';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { LinkType } from 'fym-shared/src/smartlinks/linkTypes';
import { availableSearchSources } from 'fym-common/src/types/searchService';

/**
 * The order of the rows of "Where it plays" (SoundCloud connector design d1). It is the order of
 * `SERVICE_LINK_PRIORITY` in music24-api (`PublicLinks/utils/serviceLinkPriority.ts`), the order of
 * the buttons of a SmartLink. Keep the two in step. The order is fixed: a linked row never moves.
 */
export const SERVICES_PANEL_ORDER: readonly LinkType[] = [
  LinkType.spotify,
  LinkType.applemusic,
  LinkType.youtubemusic,
  LinkType.youtube,
  LinkType.tidal,
  LinkType.deezer,
  LinkType.amazonmusic,
  LinkType.soundcloud,
  LinkType.qobuz,
  LinkType.napster,
  LinkType.pandora,
  LinkType.anghami,
  LinkType.boomplay,
  LinkType.jiosaavn,
  LinkType.gaana,
  LinkType.resso,
  LinkType.audiomack,
  LinkType.audius,
  LinkType.qqmusic,
  LinkType.yandexmusic,
  LinkType.vkmusic,
  LinkType.zvuk,
];

/** One service of the panel: the key of `serviceLinks` and the short name ("VK Music"). */
export interface PanelService {
  integrationType: MusicAPIIntegrationID;
  name: string;
}

/** The services of the panel: the services with `isSupported` (15 today; Napster closed in January 2026), in the fixed order. */
export const PANEL_SERVICES: readonly PanelService[] = SERVICES_PANEL_ORDER.flatMap((linkType) => {
  const service = allMusicServices.find(
    (one) => one.linkType === linkType && one.isSupported && one.musicAPIID !== undefined
  );
  return service === undefined || service.musicAPIID === undefined
    ? []
    : [{ integrationType: service.musicAPIID, name: service.shortName }];
});

/**
 * True when "Find it" can search this service: the services of the MusicAPI search with a company
 * account (design d4). The others open the "not searchable" state with a paste field.
 */
export function isSearchableService(integrationType: string): boolean {
  return (availableSearchSources as readonly string[]).includes(integrationType);
}

/** The row that holds the "Spotify extras" box with the Canvas line (design spotify-canvas d3). */
export const SPOTIFY_EXTRAS_SERVICE = MusicAPIIntegrationID.Spotify;

/** The row that holds "YouTube extras": the full-song videos (design service-extras a1). */
export const YOUTUBE_EXTRAS_SERVICE = MusicAPIIntegrationID.Youtube;

/** The row that holds "SoundCloud extras": the song upload (design service-extras a1). */
export const SOUNDCLOUD_EXTRAS_SERVICE = MusicAPIIntegrationID.SoundCloud;

/** The rows with an extras box: they never fold under "Show N more services". */
export const EXTRAS_HOST_SERVICES: ReadonlySet<string> = new Set([
  SPOTIFY_EXTRAS_SERVICE,
  YOUTUBE_EXTRAS_SERVICE,
  SOUNDCLOUD_EXTRAS_SERVICE,
]);

/** The icon inside the 30 px mark box of a row: a small inset, so no mark touches the box edge. */
export const MARK_ICON_SIZE = 26;
